import { useEffect, useState } from 'react';
import { Typography } from '@material-ui/core';
import { InfoCard, Link, Progress, StatusError, StatusOK, StatusWarning, Table, TableColumn, WarningPanel } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';

type Env = 'dev' | 'qa' | 'prod';
type EnvVersion = { tag?: string; health: string; syncedAt?: string };
export type VersionRow = {
  service: string;
  slug: string;
  envs: Partial<Record<Env, EnvVersion>>;
  latest?: { tag: string; date: string; author: string; url: string };
};
type Data = { updatedAt?: string; argoErrors: Partial<Record<Env, string>>; rows: VersionRow[] };

const ENVS: Env[] = ['dev', 'qa', 'prod'];
const date = (iso?: string) => (iso ? new Date(iso).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' }) : '');
// Fecha del nombre del tag (av/bv/vAAAAMMDD.N): sirve para saber qué ambiente va por detrás.
const order = (tag?: string) => tag?.match(/^(?:av|bv|v)(\d{8})\.(\d+)$/)?.slice(1).map((p, i) => (i ? p.padStart(6, '0') : p)).join('') ?? '';

/** qa va por detrás de dev cuando dev tiene un tag más reciente. */
export const qaBehind = (row: VersionRow) => {
  const dev = order(row.envs.dev?.tag);
  const qa = order(row.envs.qa?.tag);
  return Boolean(dev && qa && dev > qa);
};

const healthOf = (v: EnvVersion, tag: JSX.Element) => {
  if (v.health === 'Healthy') return <StatusOK>{tag}</StatusOK>;
  if (v.health === 'Degraded') return <StatusError>{tag} · Degraded</StatusError>;
  return (
    <StatusWarning>
      {tag} · {v.health}
    </StatusWarning>
  );
};

const cell = (v: EnvVersion | undefined, error?: string) => {
  if (error) return <Typography variant="caption" color="textSecondary">Sin acceso a Argo</Typography>;
  if (!v) return <Typography variant="caption" color="textSecondary">No desplegado</Typography>;
  return (
    <>
      {healthOf(v, <code>{v.tag ?? '—'}</code>)}
      <Typography variant="caption" color="textSecondary" component="div">
        {date(v.syncedAt)}
      </Typography>
    </>
  );
};

/** Versión de cada msa de la iniciativa en dev, qa y prod (Argo CD) y su último tag de GitHub; la ve cualquier usuario. */
export const InitiativeVersionsCard = () => {
  const { entity } = useEntity();
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const [data, setData] = useState<Data>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    (async () => {
      const base = await discovery.getBaseUrl('releases');
      const res = await fetch(`${base}/initiative?system=${encodeURIComponent(stringifyEntityRef(entity))}`);
      if (!res.ok) throw new Error(`El backend respondió ${res.status}`);
      setData(await res.json());
    })().catch(e => setError(String(e?.message ?? e)));
  }, [discovery, fetch, entity]);

  const columns: TableColumn<VersionRow>[] = [
    {
      title: 'MSA',
      field: 'service',
      render: r => (
        <>
          <Link to={`https://github.com/${r.slug}`}>{r.service}</Link>
          {qaBehind(r) && (
            <Typography variant="caption" color="textSecondary" component="div">
              qa por detrás de dev
            </Typography>
          )}
        </>
      ),
    },
    ...ENVS.map(env => ({ title: `${env} (Argo CD)`, render: (r: VersionRow) => cell(r.envs[env], data?.argoErrors[env]) })),
    {
      title: 'Último tag (GitHub)',
      render: r =>
        r.latest ? (
          <>
            <Link to={r.latest.url}>
              <code>{r.latest.tag}</code>
            </Link>
            <Typography variant="caption" color="textSecondary" component="div">
              {date(r.latest.date)} · {r.latest.author}
            </Typography>
          </>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <InfoCard title="Versiones por ambiente" subheader={data?.updatedAt ? `Argo CD y GitHub · actualizado ${date(data.updatedAt)} (cada 10 min)` : 'Argo CD y GitHub'}>
      {error && <WarningPanel title="No se pudieron cargar las versiones" message={error} />}
      {!data && !error && <Progress />}
      {data && (
        <Table<VersionRow>
          options={{ paging: false, search: false, padding: 'dense', toolbar: false }}
          columns={columns}
          data={data.rows}
          emptyContent={<div style={{ padding: 16 }}>Aún no hay datos: la primera lectura tarda hasta 10 minutos tras desplegar Backstage.</div>}
        />
      )}
    </InfoCard>
  );
};
